import { useQuery } from '@tanstack/react-query'
import { searchOffline, searchOnline } from '../lib/search-service'
import { useOffline } from '@/components/offline-context'
import { useSettings } from './useSettings'
import { useLocale } from 'next-intl'
import type { SearchResult } from '../types/dictionary'

interface WordSearchResult {
  beginsWith: SearchResult[]
  contains: SearchResult[]
}

export function useWordSearch(text: string) {
  const { isOfflineEnabled, isReady, isQueryEnabled } = useOffline()
  const { settings } = useSettings()
  const uiLocale = useLocale()
  const isOffline = isOfflineEnabled && isReady

  // Compute effective definition locale
  const effectiveLocale =
    settings.definitionLocale === 'auto'
      ? uiLocale === 'es'
        ? 'es'
        : 'en' // Default to English for non-Spanish UI locales
      : settings.definitionLocale

  return useQuery<WordSearchResult>({
    queryKey: ['wordSearch', text, settings.scriptDisplay, effectiveLocale],
    queryFn: async () => {
      const cleanText = text.trim() // FIX: Trim input for logic checks
      if (!cleanText) {
        return { beginsWith: [], contains: [] }
      }

      const isTraditionalSearch =
        settings.scriptDisplay === 'traditional' ||
        (settings.scriptDisplay === 'both' &&
          settings.scriptPriority === 'traditional')

      const results = isOffline
        ? await searchOffline(cleanText, false)
        : await searchOnline(cleanText, false, 0, 50, null, effectiveLocale)

      const beginsWith: SearchResult[] = []
      const contains: SearchResult[] = []

      for (const result of results) {
        const targetForm =
          isTraditionalSearch && result.traditional
            ? result.traditional
            : result.simplified

        // FIX: Check startsWith against the TRIMMED text
        if (targetForm && targetForm.startsWith(cleanText)) {
          beginsWith.push(result)
        } else {
          contains.push(result)
        }
      }

      return { beginsWith, contains }
    },
    enabled: isQueryEnabled(text.trim().length > 0),
    staleTime: 1000 * 60 * 30, // Cache for 30 minutes - dictionary data rarely changes
    placeholderData: (previousData) => previousData,
    gcTime: 1000 * 60 * 60, // Keep in cache for 1 hour
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  })
}
